// @flow

import R from "ramda";
import { extendObservable, action, runInAction, computed, observable } from "mobx";
import axios from "axios";
import PaginationState from "../../../models/PaginationState";
import CurrentUser from "../../../models/User";
import NetworkState from "../../../models/NetworkState";
import type { PageQuery, PaginationInfo } from "../../../models/PaginationState";
import UserList from "../../../models/UserList";
import FilterObject, { FILTER_COLUMN } from "../../../models/Filter";
import {
  CreatedOnFilter,
  UserNameFilter,
  ActiveFilter,
  ClientUserFilter,
} from "../../../models/FilterState";
import { consolidateAppliedFilters, consolidateAppliedSorts } from "../SupportFunctions";
import ModalState from "../../../models/ModalState";
import Sort, { SORT_DIRECTION } from "../../../models/Sort";
import type { FetchGraphQL, FetchAPI } from "../../../App";

const clientUserCriteriaQuery = `
query allClients {
  viewer {
    allClients(order : [{direction : ASC, field: NAME}]) {
      edges {
        node {
          clientId
          name
        }
      }
    }
  }
}
`;

export class UserListComponentStore {
  showHelpModal: boolean;
  getUsers: (PageQuery) => Promise<PaginationInfo>;
  network: NetworkState;
  pagination: PaginationState;
  totalCount: any;
  usersList: UserList[];
  usersListView: UserList[];
  usersListViewState: Object;
  createdOnFilter: CreatedOnFilter;
  userFilter: UserNameFilter;
  clientUserFilter: ClientUserFilter;
  activeFilter: ActiveFilter;
  currentUser: CurrentUser;
  defaultFilters: {
    [key: FilterColumn]: FilterObject,
  };
  appliedFilters: {
    [key: FilterColumn]: FilterObject,
  };
  appliedSorts: {
    [key: FilterColumn]: Sort,
  };
  isFiltered: boolean;
  appliedSortsOrder: Array<FilterColumn>;
  unSelectedUsers: [];

  showHelp: () => void;
  hideHelp: () => void;
  applyDefaultFilter: (FilterColumn, FilterObject) => void;
  applyFilter: (FilterColumn, FilterObject) => void;
  removeFilter: (FilterColumn) => void;
  applySort: (FilterColumn, Sort) => void;
  removeSort: (FilterColumn) => void;
  clearFilters: () => void;
  isEditing: ?boolean;
  handleStartEdit: () => void;
  handleStopEdit: () => void;
  toggleSelectAllPage: (Object) => void;
  selectAllPage: (Event) => void;
  deselectAllPage: (Event) => void;
  clearAllSelections: () => void;
  allSelected: boolean;
  allSelectedfilter: boolean;
  allOnPageSelected: boolean;
  allowMultipleItemSelection: boolean;
  confirmDeleteModal: ModalState;
  helpModal: ModalState;
  getSelectedUsersList: () => Array<string>;
  getFilterCriteriaQuery: (FilterColumn) => GraphQLQuery;
  processFilterCriteria: (FilterColumn, Object) => Array<Object>;
  applyDefaultSort: () => void;
  isTagView: ?boolean;
  sevenUrl: string;
  clientId: number;
  selectAllOnPageItem: () => void;
  fetchGraphQL: FetchGraphQL;

  constructor(fetchGraphQL: FetchGraphQL) {
    this.fetchGraphQL = fetchGraphQL;
    this.getUsers = action(this.getUsers.bind(this));

    this.applyFilter = action(this.applyFilter.bind(this));
    this.applySort = action(this.applySort.bind(this));
    this.removeFilter = action(this.removeFilter.bind(this));
    this.removeSort = action(this.removeSort.bind(this));
    this.unSelectedUsers = [];

    extendObservable(this, {
      clientId: null,
      helpModal: new ModalState(),
      network: new NetworkState(),
      pagination: new PaginationState(this.getUsers),
      totalCount: 0,
      usersList: [],
      allSelected: false,
      allSelectedfilter: false,
      usersListViewState: observable.map({}),
      usersListView: computed(() => {
        return this.usersList.map((user) => {
          if (this.usersListViewState.has(user.userId)) {
            user.viewState = this.usersListViewState.get(user.userId);

            return user;
          }

          return user;
        });
      }),
      allOnPageSelected: computed(() => {
        const allTrue = R.all(R.equals(true));
        const selectedValues = this.usersListView.map(
          (usersListView) => usersListView.viewState.selected
        );

        if (selectedValues.length === 0) {
          return false;
        }

        return allTrue(selectedValues);
      }),
      appliedSortsOrder: observable.shallow([]),
      createdOnFilter: new CreatedOnFilter(
        this,
        FILTER_COLUMN.REGION_DATE_RANGE,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      userFilter: new UserNameFilter(
        this,
        FILTER_COLUMN.REGION_LABEL,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      activeFilter: new ActiveFilter(
        this,
        FILTER_COLUMN.ACTIVE,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      clientUserFilter: new ClientUserFilter(
        this,
        FILTER_COLUMN.CLIENT_USER_LIST,
        this.getFilterCriteriaQuery,
        this.processFilterCriteria,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      defaultFilters: {},
      appliedFilters: {},
      appliedSorts: {},
      isFiltered: false,
      isEditing: null,
      allowMultipleItemSelection: true,
      selectedCount: computed(() => {
        const selectedValues = this.usersListView.map((user) => user.viewState.selected);

        if (this.allSelected) {
          return this.pagination.totalCount;
        }

        let count = 0;

        selectedValues.forEach((v) => {
          if (v) {
            count += 1;
          }
        });

        return count;
      }),
      confirmDeleteModal: new ModalState(),
      isTagView: null,
    });

    this.currentUser = null;
    this.applyDefaultFilter = action(this.applyDefaultFilter.bind(this));
    this.handleStartEdit = action(this.handleStartEdit.bind(this));
    this.handleStopEdit = action(this.handleStopEdit.bind(this));
    this.clearFilters = action(this.clearFilters.bind(this));
    this.toggleSelectAllPage = action(this.toggleSelectAllPage.bind(this));
    this.selectAllPage = action(this.selectAllPage.bind(this));
    this.deselectAllPage = action(this.deselectAllPage.bind(this));
    this.clearAllSelections = action(this.clearAllSelections.bind(this));
    this.getSelectedUsersList = action(this.getSelectedUsersList.bind(this));
    this.getFilterCriteriaQuery = action(this.getFilterCriteriaQuery.bind(this));
    this.processFilterCriteria = action(this.processFilterCriteria.bind(this));
    this.showHelp = action(this.showHelp.bind(this));
    this.hideHelp = action(this.hideHelp.bind(this));
    this.applyDefaultSort = action(this.applyDefaultSort.bind(this));
    this.selectAllOnPageItem = action(this.selectAllOnPageItem.bind(this));
    //this.applyDefaultSort();
  }

  applyDefaultSort() {
    this.activeFilter.sortState.direction = SORT_DIRECTION.ASC;
    this.activeFilter.sort = this.activeFilter.buildQuerySort();
    this.applySort(this.activeFilter.column, this.activeFilter.sort);
  }

  showHelp() {
    this.showHelpModal = true;
  }

  hideHelp() {
    this.showHelpModal = false;
  }

  handleStartEdit() {
    this.isEditing = true;
    this.usersListView.forEach((user) => user.toggleEdit());
  }

  handleStopEdit() {
    this.isEditing = false;
    this.allSelected = false;
    this.allSelectedfilter = false;
    this.usersListViewState.forEach((viewState) => {
      viewState.selected = false;
      viewState.editing = false;
    });
  }

  toggleSelectAllPage(e: Object) {
    if (!this.allowMultipleItemSelection) return;

    const setValue = !this.allOnPageSelected;

    this.usersListView.forEach((user) => {
      user.toggleSelected(e, null, null, setValue);
    });

    // When All items selected flag is up, clear selection
    if (setValue === false && this.allSelected) this.allSelected = false;
  }

  selectAllPage(e: Event) {
    this.unSelectedUsers = [];

    this.allSelected = true;
    this.allSelectedfilter = true;
  }

  selectAllOnPageItem(e: Event) {
    this.usersListView.forEach((user) => {
      user.toggleSelected(e, null, null, true);
    });
  }

  deselectAllPage(e: Event) {
    this.usersListView.forEach((user) => {
      user.toggleSelected(e, null, null, false);
    });

    this.allSelected = false;
    this.allSelectedfilter = false;
  }

  clearAllSelections() {
    this.isTagView = false;
    //  this.isEditing = false;
    this.allSelected = false;
    this.allSelectedfilter = false;
    this.usersListViewState.forEach((value) => {
      value.selected = false;
    });
  }

  applyFilter(column: FilterColumn, filter: FilterObject) {
    this.appliedFilters[column] = filter;
    this.isFiltered = true;
  }

  applyDefaultFilter(column: FilterColumn, filter: FilterObject) {
    this.defaultFilters[column] = filter;
  }

  removeFilter(column: FilterColumn) {
    delete this.appliedFilters[column];

    let entries = Object.entries(this.appliedFilters);
    if (!entries.length) this.isFiltered = false;
  }

  applySort(column: FilterColumn, sort: Sort) {
    this.appliedSorts[column] = sort;

    const index = this.appliedSortsOrder.indexOf(column);
    if (index === -1) this.appliedSortsOrder.push(column);
  }

  removeSort(column: FilterColumn) {
    delete this.appliedSorts[column];

    const index = this.appliedSortsOrder.indexOf(column);
    if (index > -1) this.appliedSortsOrder.splice(index, 1);
  }

  //clear all filters
  clearFilters() {
    this.userFilter = new UserNameFilter(
      this,
      FILTER_COLUMN.REGION_LABEL,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.createdOnFilter = new CreatedOnFilter(
      this,
      FILTER_COLUMN.REGION_DATE_RANGE,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.activeFilter = new ActiveFilter(
      this,
      FILTER_COLUMN.ACTIVE,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.clientUserFilter = new ClientUserFilter(
      this,
      FILTER_COLUMN.CLIENT_USER_LIST,
      this.getFilterCriteriaQuery,
      this.processFilterCriteria,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );

    this.appliedFilters = observable({});
    this.appliedSorts = observable({});
    this.appliedSortsOrder.length = 0;
    this.isFiltered = false;
    this.applyDefaultSort();

    return this.pagination.goFetch(null);
  }

  //creating country filter query
  getFilterCriteriaQuery(column: FilterColumn): GraphQLQuery {
    switch (column) {
      case FILTER_COLUMN.CLIENT_USER_LIST:
        return {
          query: clientUserCriteriaQuery,
          variables: {},
        };

      default:
        return null;
    }
  }

  //process country filter result data
  processFilterCriteria(column: FilterColumn, payload: Object): Array<any> {
    switch (column) {
      case FILTER_COLUMN.CLIENT_USER_LIST:
        const clientowners: [
          {
            clientId: string,
            name: string,
          }
        ] = payload.data.viewer.allClients.edges;
        let processedClientOwners = observable.map({});
        clientowners.forEach((owner) => {
          processedClientOwners.set(owner.node.clientId, {
            id: owner.node.clientId,
            ...owner,
          });
        });

        return processedClientOwners;

      default:
        return null;
    }
  }

  toggleAllItems() {
    if (!this.allowMultipleItemSelection) return;

    this.allSelected = !this.allSelected;

    if (this.allSelected === false) {
      this.projectCostsView.forEach((value) => {
        value.viewState.selected = false;
      });
    }
  }

  getSelectedUsersList(): Array<string> {
    const usersList = this.usersListViewState;

    let selectedUsersList = [];

    usersList.forEach((value, key) => {
      if (value.selected) {
        selectedUsersList.push(key);
      }
    });

    return selectedUsersList;
  }

  //function for get user list
  async getUsers(pageQuery: PageQuery): Promise<PaginationInfo> {
    let res = null;
    let params: string[] = pageQuery.params;
    let args = pageQuery.args;
    let variables = pageQuery.variables;
    let filtersCriteria: string[] = [];

    let sortCriteria: string[] = [];
    consolidateAppliedSorts(this.appliedSorts, sortCriteria);
    consolidateAppliedFilters(this.appliedFilters, params, filtersCriteria, variables);

    // NOTE: applied filters could override default filters, handle this if needed
    consolidateAppliedFilters(this.defaultFilters, params, filtersCriteria, variables);

    if (this.userFilter.textToLookFor) {
      params.push("$search:String");
      args.push("search:$search");
      variables.search = this.userFilter.textToLookFor;
    }

    if (this.clientId) {
      params.push("$clientIn:[Int]");
      filtersCriteria.push("clientIn:$clientIn");
      variables.clientIn = [parseInt(this.clientId, 10)];
    }

    const queryParams = params.join(", ");
    const queryArgs = args.join(", ");
    const queryFiltersCriteria = filtersCriteria.join(", ");
    const querySortCriteria = sortCriteria.join(", ");

    //debugger
    const query = `
      query getAllUser(${queryParams}){
        viewer{
          allUsers(${queryArgs},filters: { ${queryFiltersCriteria} }, order: [${querySortCriteria}]){
            totalCount
            edges{
              node{
                userId
                username
                firstName
                lastName
                email
                isActive
                lastLogin
                shareRateCards
                totalSearchesCount
                totalJobsCount
                totalRatecardsCount
                totalProjectsCount
                dateJoined
                client{
                	name
                }
              }
            }
          }
        }
      }
      `;

    this.network.loading = true;

    try {
      res = await this.fetchGraphQL(query, variables);
    } catch (e) {
      console.log("error", e);
      if (axios.isCancel(e)) {
        return e;
      }

      this.network.handleError("Getting users", e);
      if (res !== null) {
        this.network.logGraphQLError("Get users query", res);
      }

      // TODO: Display user friendly error message
      return e;
    }

    return runInAction("getusers--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("Get user query", res)) {
        // TODO: Display user friendly error message
        return {
          totalCount: 0,
        };
      }
      //this.currentUser = new CurrentUser(this, res.data.viewer.user);
      const users = res.data.viewer.allUsers.edges;
      this.totalCount = res.data.viewer.allUsers.totalCount;
      // // TODO: Deserialize this properly...
      this.usersList = users.map((reg) => {
        const user = new UserList(this, reg.node);
        if (!this.usersListViewState.has(user.userId)) {
          this.usersListViewState.set(user.userId, {
            selected: this.allSelected,
            editing: this.isEditing,
          });
        } else {
          const selectedValue = this.allSelected
            ? true
            : this.usersListViewState.get(user.userId).selected;

          this.usersListViewState.set(user.userId, {
            selected: selectedValue,
            editing: this.isEditing,
          });
        }

        user.viewState = this.usersListViewState.get(user.userId);

        return user;
      });
      return {
        totalCount: res.data.viewer.allUsers.totalCount,
      };
    });
  }
}

export default class UserListStore extends UserListComponentStore {
  exportUsers: () => void;
  fetchAPI: FetchAPI;

  constructor(fetchGraphQL: FetchGraphQL, fetchAPI: FetchAPI) {
    super(fetchGraphQL);
    this.fetchAPI = fetchAPI;

    this.exportUsers = action(this.exportUsers.bind(this));
  }

  exportUsers() {
    if (this.network.loading) {
      return;
    }

    let params = [];
    let args = [];
    let vars = {};
    let exportURL = "users/basic/list/export/excel/";
    // Change the Filename
    var fileName = "export_users_" + Math.floor(Math.random() * 9999999 + 1000000);
    let parameters = "";
    let exparams = "";

    if (this.allSelectedfilter) {
      consolidateAppliedFilters(this.appliedFilters, params, args, vars);
      vars.exclude = [];
      parameters = JSON.stringify(vars);
      if (parameters === "") {
        exparams = `{"fileName": "${fileName}","section":"admin" , "userFilters": {${parameters}}}`;
      } else {
        exparams = `{"fileName": "${fileName}","section":"admin" , "userFilters": ${parameters}}`;
      }
    } else {
      const selectedUsers = this.getSelectedUsersList();
      if (!selectedUsers || !selectedUsers.length) {
        console.error("Cannot export Users: No Users selected");
        return;
      }
      vars.only = selectedUsers;
      parameters = JSON.stringify(vars);
      if (parameters === "") {
        exparams = `{"fileName": "${fileName}","section":"admin" , "userFilters": {${parameters}}}`;
      } else {
        exparams = `{"fileName": "${fileName}","section":"admin" , "userFilters": ${parameters}}`;
      }
    }

    this.fetchAPI(exportURL, JSON.parse(exparams))
      .then((res) => {
        window.location.href = res.data.url;
        this.handleStopEdit();
      })
      .catch((e) => {
        console.log("Error downloading excel", e);
        throw e;
      });
  }
}
